import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db, storage } from "../firebase";  // ⬅️ make sure storage is exported in firebase.js
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc, getDocs, collection, query, where, Timestamp, getDoc } from "firebase/firestore";
import { ref, listAll, getBytes, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "react-toastify/dist/ReactToastify.css";
import { FiEye, FiEyeOff } from "react-icons/fi";
import TermsAndConditions from "../components/TermsAndConditions";

// ✅ Reusable Input Field
const InputField = ({ label, type = "text", value, setValue, error, isRequired = false }) => (
  <div>
    <label className="block text-gray-800 mb-1 text-sm">
      {label} {isRequired && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="w-full p-3 rounded-lg border border-gray-300"
      required={isRequired}
    />
    {error && <p className="text-red-500 text-sm">{error}</p>}
  </div>
);

// ✅ Reusable Password Field with Toggle
const PasswordField = ({ label, value, setValue, error, isRequired = false }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div>
      <label className="block text-gray-800 mb-1 text-sm">
        {label} {isRequired && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full p-3 rounded-lg border border-gray-300 pr-10"
          required={isRequired}
        />
        <button type="button" className="absolute right-3 top-3 text-gray-500" onClick={() => setShowPassword(!showPassword)}>
          {showPassword ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

// ✅ Modal
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Terms and Conditions</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="border-t border-gray-200 pt-4">{children}</div>
      </div>
    </div>
  );
};

// ✅ Chargebee customer creation
const createChargebeeCustomer = async (uid, email) => {
  try {
    const response = await fetch("https://login.styleuai.com/api/chargebee/create-customer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uid, email }),
    });

    if (!response.ok) throw new Error("Failed to create Chargebee customer");
    const data = await response.json();
    return data.customerId;
  } catch (error) {
    console.error("Chargebee Error:", error);
    return null;
  }
};

// ✅ Send token to backend
const sendTokenToBackend = async (token) => {
  try {
    const response = await fetch("https://login.styleuai.com/api/auth/verify-token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Token verification failed with status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Token Error:", error);
    // Don't throw error - token verification failure shouldn't break signup flow
    return null;
  }
};

// ✅ Send admin notification when user signs up
const sendAdminSignupNotification = async (userData) => {
  try {
    console.log("📧 Preparing to send admin notification with data:", userData);
    
    // Validate required fields
    if (!userData.firstName || !userData.email || !userData.uid) {
      console.error("❌ Missing required fields for admin notification:", {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        uid: userData.uid
      });
      return;
    }

    const functions = getFunctions();
    const sendAdminNotification = httpsCallable(functions, 'sendAdminSignupNotification');
    
    const notificationData = {
      firstName: userData.firstName.trim(),
      lastName: userData.lastName ? userData.lastName.trim() : '',
      email: userData.email.trim(),
      contactNumber: userData.contactNumber || 'Not provided',
      uid: userData.uid
    };
    
    console.log("📧 Sending admin notification with data:", notificationData);
    
    await sendAdminNotification(notificationData);
    
    console.log("✅ Admin notification sent successfully");
  } catch (error) {
    console.error("❌ Error sending admin notification:", error);
    // Don't throw error here - admin notification failure shouldn't break signup flow
  }
};

const copyDefaultModels = async (uid) => {
  try {
    const defaultsRef = ref(storage, "Defaults/Models");
    const result = await listAll(defaultsRef);

    const modelLinks = [];

    for (const itemRef of result.items) {
      // 🔑 Get URL of the default file
      const defaultUrl = await getDownloadURL(itemRef);

      // ⬇️ Fetch the file manually (this triggers CORS if not configured!)
      const response = await fetch(defaultUrl);
      const blob = await response.blob();

      // ⬆️ Upload to user's folder
      const targetRef = ref(storage, `assets/${uid}/models/${itemRef.name}`);
      await uploadBytes(targetRef, blob);

      const newUrl = await getDownloadURL(targetRef);
      modelLinks.push(newUrl);
    }

    await setDoc(doc(db, "users", uid), { models: modelLinks }, { merge: true });

    console.log("✅ Default models copied & URLs saved!");
  } catch (err) {
    console.error("Error copying default models:", err);
    toast.warn("⚠️ Account created, but default models could not be copied.");
  }
};
// ✅ Signup Component
const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [otpSent, setOtpSent] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [emailCheckInterval, setEmailCheckInterval] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (emailCheckInterval) clearInterval(emailCheckInterval);
    };
  }, [emailCheckInterval]);

  const checkEmailExists = async (email) => {
    try {
      const q = query(collection(db, "users"), where("email", "==", email));
      const querySnapshot = await getDocs(q);
  
      if (!querySnapshot.empty) {
        return true; // Email already exists
      }
      return false;
    } catch (err) {
      console.error("Email check error:", err);
      toast.error("Error checking email availability. Please try again.");
      return false;
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const errors = {};
    if (!firstName.trim()) errors.firstName = "First name is required.";
    if (!lastName.trim()) errors.lastName = "Last name is required.";
    if (!email.trim()) errors.email = "Email is required.";
    if (!validateEmail(email)) errors.email = "Invalid email format.";
    if (!contactNumber) errors.contactNumber = "Contact number is required.";
    if (!password) errors.password = "Password is required.";
    if (password.length < 6) errors.password = "Minimum 6 characters required.";
    if (password !== confirmPassword) errors.confirmPassword = "Passwords do not match.";
    if (!agreeToTerms) errors.terms = "Please accept Terms & Conditions.";
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      toast.error("Please fix the highlighted errors before submitting.");
      return;
    }
  
    setLoading(true);
    setErrors({});
  
    try {
      const emailExists = await checkEmailExists(email);
      if (emailExists) {
        setErrors({ email: "Email already registered." });
        toast.error("This email is already registered. Please login instead.");
        setLoading(false);
        return;
      }
  
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      try {
        await sendEmailVerification(user);
        toast.success("Verification email sent! Please check your inbox.");
        setVerificationSent(true);
        setOtpSent(true);
      } catch (verifyErr) {
        console.error("Email verification error:", verifyErr);
        toast.error("Failed to send verification email. Try again later.");
      }
  
      const interval = setInterval(async () => {
        await user.reload();
        if (user.emailVerified) {
          clearInterval(interval);
          toast.success("✅ Email verified! Redirecting...");
          navigate("/PricingPlan", { replace: true });
        }
      }, 4000);
      setEmailCheckInterval(interval);
  
      const chargebeeCustomerId = await createChargebeeCustomer(user.uid, email);

  
      try {
        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          firstName,
          lastName,
          email,
          contactNumber,
          activePlan: "free",
          planType: "monthly",
          credits: 5,
          designRequestsLeft: 0,
          isSubscribed: false,
          lastPaymentAt: null,
          previousPlan: null,
          isPlanDowngrade: false,
          planChangeDate: null,
          hasUsedTrial: false,
          paymentDetails: {
            planType: null,
            subscriptionStart: null,
            subscriptionEnd: null,
            nextBillingDate: null,
          },
          chargebeeCustomerId: chargebeeCustomerId || null,
          chargebeeSubscriptionId: null,
          createdAt: Timestamp.now(),
          emailVerified: false,
        });

        // ⬇️ Copy default models after user doc creation
        await copyDefaultModels(user.uid);

        // 📧 Send admin notification about new signup
        await sendAdminSignupNotification({
          firstName,
          lastName,
          email,
          contactNumber,
          uid: user.uid
        });

      } catch (dbErr) {
        console.error("Firestore error:", dbErr);
        toast.error("⚠️ Account created, but saving details failed. Contact support.");
      }
  
      try {
        const idToken = await user.getIdToken();
        const tokenResult = await sendTokenToBackend(idToken);
        if (!tokenResult) {
          toast.warn("⚠️ Account created, but token sync failed. Please relogin later.");
        }
      } catch (tokenErr) {
        console.error("Token error:", tokenErr);
        toast.warn("⚠️ Account created, but token sync failed. Please relogin later.");
      }
  
    } catch (err) {
      console.error("Signup error:", err);
      let message = "Signup failed. Please try again.";
  
      if (err.code === "auth/email-already-in-use") {
        message = "This email is already registered. Please login instead.";
      } else if (err.code === "auth/invalid-email") {
        message = "Invalid email address format.";
      } else if (err.code === "auth/weak-password") {
        message = "Password must be at least 6 characters.";
      } else if (err.code === "auth/network-request-failed") {
        message = "Network error. Please check your internet connection.";
      }
  
      setErrors({ general: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("No active user found. Please log in again.");
        return;
      }
  
      await sendEmailVerification(user);
      toast.success("Verification email resent successfully. Please check your inbox.");
  
      setVerificationSent(true);
      setResendDisabled(true);
      setResendTimer(60);
      setShowResendVerification(true);
  
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
  
    } catch (err) {
      console.error("Resend verification error:", err);
      let message = "Failed to resend verification email. Please try again later.";
  
      if (err.code === "auth/too-many-requests") {
        message = "Too many resend attempts. Please wait before trying again.";
      } else if (err.code === "auth/network-request-failed") {
        message = "Network error. Please check your connection.";
      }
  
      toast.error(message);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-gradient-to-br from-[#f7faff] to-[#e8f0ff] sm:px-6 sm:pt-5">
      <div className="absolute top-5 left-5">
        <a href="https://styleuai.com" target="_blank" rel="noopener noreferrer">
          <img src="/logo.png" alt="StyleAI Logo" className="h-10 w-auto drop-shadow-md hover:scale-105 transition-transform duration-300" />
        </a>
      </div>

      <div className="w-full max-w-lg bg-white bg-opacity-10 backdrop-blur-lg p-8 rounded-2xl shadow-2xl border border-white/20">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Join Us Today!</h2>

        {errors.general && <p className="text-red-500 text-center mb-4">{errors.general}</p>}

        {verificationSent && showResendVerification && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            <p>Verification email sent! Check your inbox.</p>
            <button onClick={handleResendVerification} disabled={resendDisabled} className="mt-2 text-green-700 hover:text-green-800 font-semibold disabled:opacity-50">
              {resendDisabled ? `Resend in ${resendTimer}s` : "Resend Verification Email"}
            </button>
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-5">
          <InputField label="First Name" value={firstName} setValue={setFirstName} error={errors.firstName} isRequired />
          <InputField label="Last Name" value={lastName} setValue={setLastName} error={errors.lastName} isRequired />
          <InputField label="Email" type="email" value={email} setValue={setEmail} error={errors.email} isRequired />

          <div>
            <label className="block text-gray-800 mb-1 text-sm">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <PhoneInput
              country={"in"}
              value={contactNumber}
              onChange={setContactNumber}
              containerClass="w-full"
              inputClass="w-full p-3 rounded-lg border border-gray-300"
              required
            />
            {errors.contactNumber && <p className="text-red-500 text-sm">{errors.contactNumber}</p>}
          </div>

          <PasswordField label="Password" value={password} setValue={setPassword} error={errors.password} isRequired />
          <PasswordField label="Confirm Password" value={confirmPassword} setValue={setConfirmPassword} error={errors.confirmPassword} isRequired />

          <div className="flex items-center text-gray-800">
            <input type="checkbox" className="mr-2" checked={agreeToTerms} onChange={() => setAgreeToTerms(!agreeToTerms)} required />
            <p className="text-sm">
              I agree to the{" "}
              <button type="button" className="text-indigo-600 hover:text-indigo-800 font-medium underline" onClick={() => setShowTerms(true)}>
                Terms and Conditions
              </button>
            </p>
          </div>
          {errors.terms && <p className="text-red-500 text-sm">{errors.terms}</p>}

          <button type="submit" className="w-full bg-indigo-500 text-white py-3 rounded-lg font-semibold transition-all duration-300 hover:bg-indigo-600 active:scale-95 disabled:opacity-50" disabled={loading}>
            {loading ? "Signing up..." : "Sign Up"}
          </button>

          {otpSent && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mt-6 text-center">
              <p>📧 Verification link sent. You’ll be redirected once verified.</p>
              <button onClick={handleResendVerification} disabled={resendDisabled} className="mt-3 text-indigo-600 hover:underline font-medium disabled:opacity-50">
                {resendDisabled ? `Resend in ${resendTimer}s` : "Resend Verification Email"}
              </button>
            </div>
          )}

          <div className="text-center mt-4">
            <p className="text-gray-800 text-sm">
              Already registered?{" "}
              <Link to="/" className="text-indigo-600 hover:text-indigo-800 font-medium underline">
                Login now
              </Link>
            </p>
          </div>
        </form>

        {showTerms && <Modal isOpen={showTerms} onClose={() => setShowTerms(false)}><TermsAndConditions /></Modal>}
      </div>
    </div>
  );
};

export default Signup;
